<template>
  <main class="home">
    <Registration
      :creditLineContract="creditLineContract"
      :termsAndConditions="termsAndConditions"
      :privacyPolicy="privacyPolicy"
      :hardOneFunnel="hardOneFunnel"
      :creditLineOfferFields="creditLineOfferFields"
    />
  </main>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, provide, ref } from "vue";
import Registration from "@/apps/credit-line/components/Registration.vue";
import { setLanguage } from "@/apps/credit-line/helpers/translations.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { Service } from "@/apps/credit-line/services/network.service";
import { ConsumerSettingsService } from "@/shared-services/consumer/settings.service";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import {
  DEFAULT_SETTING,
  SettingValue,
} from "@/shared-models/consumer/ISetting";
import percentageFormat from "@/apps/credit-line/helpers/percentageFormatter.helper";
import { IOffer } from "@/apps/credit-line/models/IOffer";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import { calculateEffectiveInterestRate } from "@/shared-helpers/effective-interest-rate.helper";
import { CL_COUNTRIES } from "@/apps/credit-line/types/cl_countries";

setLanguage(getCurrentInstance()?.proxy);
const apiService = new Service();
let settingsService = new ConsumerSettingsService(
  apiService,
  AuthenticationHelper,
  DatadogLogger,
  SettingsCategory.CL_REGISTRATION
);
const creditLineOfferFields = ref<SettingValue>(DEFAULT_SETTING);
const creditLineContract = ref<SettingValue>(DEFAULT_SETTING);
const termsAndConditions = ref<SettingValue>(DEFAULT_SETTING);
const privacyPolicy = ref<SettingValue>(DEFAULT_SETTING);
const hardOneFunnel = ref<SettingValue>(DEFAULT_SETTING);

const dynamicOfferFieldsFactory = computed(() => {
  try {
    if (
      !creditLineOfferFields.value?.enabled ||
      !creditLineOfferFields.value?.values?.fields
    ) {
      console.log(
        "❌ [dynamicOfferFieldsFactory] Settings not enabled or fields missing",
        creditLineOfferFields.value
      );
      return () => [];
    }

    const fields = Array.isArray(creditLineOfferFields.value?.values?.fields)
      ? creditLineOfferFields.value?.values?.fields
      : [];
    const fieldsTexts = creditLineOfferFields.value?.values?.fieldsTexts || {};

    return (userOffer: IOffer, t) => {
      return fields.map((fieldName: string) => {
        const text =
          fieldsTexts[fieldName] || t(`NEW_CL_OFFER_ITEMS.${fieldName}`);
        const rawValue = userOffer[fieldName];

        let formattedValue: string;

        if (fieldName === "weeklyInterestRate" && userOffer.country === CL_COUNTRIES.CO) {
          const annualPercentageRateDisplay = Number(userOffer.annualPercentageRateDisplay);
          const effectiveAnnualRate = annualPercentageRateDisplay
            ? annualPercentageRateDisplay
            : calculateEffectiveInterestRate(Number(userOffer.annualPercentageRate));
          const weeklyInterestRateDecimal = effectiveAnnualRate / 52;
          formattedValue = percentageFormat(weeklyInterestRateDecimal / 100);
        } else if (
          fieldName.toLowerCase().includes("rate") ||
          fieldName.toLowerCase().includes("percentage")
        ) {
          formattedValue = percentageFormat(rawValue || 0);
        } else if (typeof rawValue === "number") {
          formattedValue = rawValue.toString();
        } else {
          formattedValue = rawValue || "0";
        }

        return {
          text,
          value: formattedValue,
        };
      });
    };
  } catch (error) {
    console.error("[dynamicOfferFieldsFactory] Error:", error);
    return () => [];
  }
});

provide("creditLineOfferFields", creditLineOfferFields);
provide("dynamicOfferFieldsFactory", dynamicOfferFieldsFactory);

onMounted(async () => {
  try {
    creditLineOfferFields.value = await settingsService.getSetting(
      "creditLineOfferFields"
    );
    creditLineContract.value = await settingsService.getSetting(
      "creditLineContract"
    );
    termsAndConditions.value = await settingsService.getSetting(
      "termsAndConditions"
    );
    privacyPolicy.value = await settingsService.getSetting("privacyPolicy");
    hardOneFunnel.value = await settingsService.getSetting("hardOneFunnel");
  } catch (error) {
    console.log("Error fetching settings: ", error);
  }
});
</script>
